import { useTranslation } from 'react-i18next';
import { DocumentSettingsForm } from './DocumentSettingsForm';
import { LoadingIndicator } from './loading-indicator';
import Box from '@mui/material/Box';
import { useState } from 'react';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { SecondaryMenuWrapper } from '../component';
import { CHAT_BOT_HEIGHT } from '../component/ChatBotSpaceHolder';
import { CustomFieldsSection } from '../CustomFieldsSection/CustomFieldsSection';
import { useListCurrencyQuery } from '../../../../pages/editor/useListCurrencyQuery.tsx';
import CurrencyAutocomplete from './CurrencyAutocomplete/CurrencyAutocomplete.tsx';
import { ContentType } from 'utils/getContentType.ts';
import type { DocumentEntity } from 'services/documents/entities';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId, selectContentType } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import Typography from '@mui/material/Typography';
import type { RelatedLink } from 'pages/flex-data/flex-data-links/types';
import { LinkAnAppSection } from './LinkAnAppSection/LinkAnAppSection';
import { LegacyIntegrationsButton } from './LegacyIntegrationsButton';
import { useAssignedUserLegacyId } from 'hooks/useAssignedUserLegacyId';

export function DocumentSettings() {
  const contentId = useAppSelector(selectContentId);
  const { t } = useTranslation();

  const contentType = useAppSelector(selectContentType);
  const { data } = useContentMetadata(contentType, contentId);
  const documentId = data?.documentId;
  const { legacyId: assignedUserLegacyId, isResolving: isAssignedUserResolving } = useAssignedUserLegacyId(
    data?.assignedUser,
    data?.accountId
  );
  const { data: currencies, isLoading, error } = useListCurrencyQuery();
  const [pendingCrmRelatedLinks, setPendingCrmRelatedLinks] = useState<RelatedLink[]>();

  const evaluateFeatureFlag = useFeatureFlag([FeatureFlags.currencySettings, FeatureFlags.crmDealLinking, FeatureFlags.useVariablesBff]);
  const isCurrencySettingsEnabled = !isLoading && !error && evaluateFeatureFlag[FeatureFlags.currencySettings];
  const isCrmDealLinkingEnabled = evaluateFeatureFlag[FeatureFlags.crmDealLinking];
  const areRelevantFlagsResolved =
    evaluateFeatureFlag[FeatureFlags.crmDealLinking] !== undefined && evaluateFeatureFlag[FeatureFlags.useVariablesBff] !== undefined;

  // The form seeds its state once, so wait for the assigned user on first mount only;
  // a later refetch that turns the assignee into a UUID must not unmount an open form.
  const isFormReady = !!data && areRelevantFlagsResolved && !isAssignedUserResolving;
  const [hasShownForm, setHasShownForm] = useState(false);
  if (isFormReady && !hasShownForm) setHasShownForm(true);

  return (
    <SecondaryMenuWrapper testId={'document-settings-component'}>
      <Box sx={(theme) => ({ height: '100%', pt: 1.5, pb: `calc(${CHAT_BOT_HEIGHT} + ${theme.spacing(3)})` })}>
        {isCrmDealLinkingEnabled && contentId ? (
          <>
            <LinkAnAppSection contentId={contentId} onLinked={setPendingCrmRelatedLinks} />
            <Typography variant="subtitle2" pt={2} pb={2}>
              {t('editor.side_panel.document_info')}
            </Typography>
          </>
        ) : null}

        {data && areRelevantFlagsResolved && (isFormReady || hasShownForm) ? (
          <DocumentSettingsForm
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            data={{ id: documentId!, ...data, contentId } as DocumentEntity}
            assignedUserLegacyId={assignedUserLegacyId}
            pendingCrmRelatedLinks={pendingCrmRelatedLinks}
          />
        ) : (
          <LoadingIndicator />
        )}

        {data?.contentId && (
          <CustomFieldsSection level={contentType === ContentType.DOCUMENT ? 'document' : 'template'} id={data.contentId} />
        )}

        {isCrmDealLinkingEnabled === false ? <LegacyIntegrationsButton documentId={documentId} /> : null}

        {isCurrencySettingsEnabled && (
          <CurrencyAutocomplete currencies={currencies} autocompleteTitle={t('document.currency.document_currency')} />
        )}
      </Box>
    </SecondaryMenuWrapper>
  );
}
